import { createContext, useContext, useMemo, useState } from 'react'
import { useApiListAssetThumbnail, useApiListNodeTemplate } from '../../../../../api-new/state/content'
import { usePlatformList } from '../common/platform/use-platform-list'
import { useLoad } from '../../../../../shared/hooks/load'
import { useFirstLoadSettled } from '../../../../../shared/empty-state/empty-state'
import { DATATYPE_COURSE_TEMPLATE, DATATYPE_COURSE_TEMPLATE_CATEGORY } from '../common/shared/metadata'

const DataContext = createContext()

export const useDataContext = () => useContext(DataContext)

export const DataProvider = ({ children }) => {
  const apiListNodeTemplate = useApiListNodeTemplate()
  const apiListAssetThumbnail = useApiListAssetThumbnail()

  useLoad(async () => {
    apiListNodeTemplate.get()
    apiListAssetThumbnail.get('template')
  }, [])

  // True once the first fetch has answered, so the page can tell "still
  // loading" from "nothing here" (and reloads never flash a loading state).
  const loaded = useFirstLoadSettled(apiListNodeTemplate)

  const categories = useMemo(() => apiListNodeTemplate.items.filter(node => node.datatype === DATATYPE_COURSE_TEMPLATE_CATEGORY), [apiListNodeTemplate])
  const templates = useMemo(() => apiListNodeTemplate.items.filter(node => node.datatype === DATATYPE_COURSE_TEMPLATE), [apiListNodeTemplate])

  // "Available on": the area's list for one platform, as content answers it
  // (the templates available there and the folders above them). The
  // unfiltered lists above stay as they are for everything else.
  const platformList = usePlatformList(useApiListNodeTemplate)
  const { settled: filteredSettled, items: filteredItems, error: filteredError, reload: reloadFiltered } = platformList

  const filtered = useMemo(
    () => ({
      loaded: filteredSettled,
      error: filteredError,
      retry: reloadFiltered,
      items: filteredItems,
      categories: filteredItems.filter(node => node.datatype === DATATYPE_COURSE_TEMPLATE_CATEGORY),
      templates: filteredItems.filter(node => node.datatype === DATATYPE_COURSE_TEMPLATE)
    }),
    [filteredSettled, filteredItems, filteredError, reloadFiltered]
  )

  // "Format" (owner 2026-10-09): read from each template's metadata in the
  // browser. Like the platform, it belongs to the area.
  // The page shows and applies it to super admins only (useFormatFilter,
  // format-controls.js).
  const [format, setFormat] = useState(null)

  return (
    <DataContext.Provider
      value={{
        reload: async () => {
          await apiListNodeTemplate.get()
          apiListAssetThumbnail.get('template')
          if (platformList.platform) reloadFiltered()
        },

        platform: platformList.platform,
        setPlatform: platformList.setPlatform,
        filtered: filtered,

        format: format,
        setFormat: setFormat,

        loaded: loaded,
        error: apiListNodeTemplate.error,
        retry: () => apiListNodeTemplate.get(),
        items: apiListNodeTemplate.items,
        categories: categories,
        templates: templates,

        thumbnails: apiListAssetThumbnail.items
      }}>
      {children}
    </DataContext.Provider>
  )
}
